(() => {
  const FILTER_HASH_PREFIX = '#oe?';
  const NAV_LINK_SELECTOR = '[data-collection-anchor-nav] a[href="#artikel"], [data-collection-anchor-nav] a[href="#informationen"]';
  const SORT_SELECTOR = '.collection-anchor-pilot__sort-control';
  const sortWidgets = [];

  const updateSortWidget = (widget) => {
    const selectedOption = [...widget.select.options].find((option) => option.value === widget.select.value) || widget.select.options[0];
    if (!selectedOption) return;

    widget.value.textContent = selectedOption.textContent.trim();
    widget.options.forEach((option) => {
      const isSelected = option.dataset.value === selectedOption.value;
      option.setAttribute('aria-selected', String(isSelected));
      option.classList.toggle('collection-anchor-pilot__sort-option--selected', isSelected);
    });
  };

  const closeSortWidget = (widget, restoreFocus = false) => {
    if (widget.listbox.hidden) return;
    widget.listbox.hidden = true;
    widget.trigger.setAttribute('aria-expanded', 'false');
    widget.field.classList.remove('collection-anchor-pilot__sort-field--open');
    if (restoreFocus) widget.trigger.focus();
  };

  const closeOtherSortWidgets = (activeWidget) => {
    sortWidgets.forEach((widget) => {
      if (widget !== activeWidget) closeSortWidget(widget);
    });
  };

  const openSortWidget = (widget, preferredIndex) => {
    closeOtherSortWidgets(widget);
    widget.listbox.hidden = false;
    widget.trigger.setAttribute('aria-expanded', 'true');
    widget.field.classList.add('collection-anchor-pilot__sort-field--open');

    const selectedIndex = widget.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const targetIndex = Number.isInteger(preferredIndex) ? preferredIndex : Math.max(selectedIndex, 0);
    const target = widget.options[Math.min(Math.max(targetIndex, 0), widget.options.length - 1)];
    if (!target) return;

    requestAnimationFrame(() => {
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'nearest' });
    });
  };

  const selectSortOption = (widget, value) => {
    document.querySelectorAll(SORT_SELECTOR).forEach((select) => {
      if ([...select.options].some((option) => option.value === value)) select.value = value;
    });
    sortWidgets.forEach(updateSortWidget);
    widget.select.dispatchEvent(new Event('change', { bubbles: true }));
    closeSortWidget(widget, true);
  };

  const handleSortOptionKeydown = (event, widget, optionIndex) => {
    let nextIndex = null;
    if (event.key === 'ArrowDown') nextIndex = Math.min(optionIndex + 1, widget.options.length - 1);
    if (event.key === 'ArrowUp') nextIndex = Math.max(optionIndex - 1, 0);
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = widget.options.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      widget.options[nextIndex].focus();
      widget.options[nextIndex].scrollIntoView({ block: 'nearest' });
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectSortOption(widget, widget.options[optionIndex].dataset.value);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSortWidget(widget, true);
      return;
    }

    if (event.key === 'Tab') closeSortWidget(widget);
  };

  const enhanceSortControl = (select, index) => {
    const field = select.closest('.collection-anchor-pilot__sort-field');
    const wrapper = select.closest('.collection-anchor-pilot__sort-select');
    const label = field?.querySelector('.facet-filters__label');
    if (!field || !wrapper || !label || field.classList.contains('collection-anchor-pilot__sort-field--enhanced')) return;

    const idBase = select.id || `CollectionAnchorPilotSort-${index}`;
    const labelId = `${idBase}-label`;
    const triggerId = `${idBase}-trigger`;
    const valueId = `${idBase}-value`;
    const listboxId = `${idBase}-listbox`;
    label.id = labelId;
    label.htmlFor = triggerId;

    const trigger = document.createElement('button');
    trigger.className = 'collection-anchor-pilot__sort-trigger';
    trigger.type = 'button';
    trigger.id = triggerId;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-controls', listboxId);
    trigger.setAttribute('aria-labelledby', `${labelId} ${valueId}`);

    const value = document.createElement('span');
    value.className = 'collection-anchor-pilot__sort-value';
    value.id = valueId;
    trigger.append(value);

    const listbox = document.createElement('div');
    listbox.className = 'collection-anchor-pilot__sort-popover';
    listbox.id = listboxId;
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('aria-labelledby', labelId);
    listbox.hidden = true;

    const widget = { field, listbox, options: [], select, trigger, value };
    [...select.options].forEach((nativeOption, optionIndex) => {
      const option = document.createElement('div');
      option.className = 'collection-anchor-pilot__sort-option';
      option.dataset.value = nativeOption.value;
      option.setAttribute('role', 'option');
      option.tabIndex = -1;

      const optionLabel = document.createElement('span');
      optionLabel.textContent = nativeOption.textContent.trim();
      const check = document.createElement('span');
      check.className = 'collection-anchor-pilot__sort-check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '\u2713';
      option.append(optionLabel, check);
      option.addEventListener('click', () => selectSortOption(widget, nativeOption.value));
      option.addEventListener('keydown', (event) => handleSortOptionKeydown(event, widget, optionIndex));
      widget.options.push(option);
      listbox.append(option);
    });

    const caret = wrapper.querySelector('.svg-wrapper');
    wrapper.insertBefore(trigger, caret || null);
    wrapper.append(listbox);
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    field.classList.add('collection-anchor-pilot__sort-field--enhanced');
    sortWidgets.push(widget);
    updateSortWidget(widget);

    trigger.addEventListener('click', () => {
      if (listbox.hidden) openSortWidget(widget);
      else closeSortWidget(widget, true);
    });
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSortWidget(widget, true);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const selectedIndex = widget.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      const targetIndex = event.key === 'Home' ? 0 : event.key === 'End' ? widget.options.length - 1 : Math.max(selectedIndex, 0);
      openSortWidget(widget, targetIndex);
    });
  };

  const setActiveLink = (targetHash) => {
    document.querySelectorAll(NAV_LINK_SELECTOR).forEach((link) => {
      if (link.getAttribute('href') === targetHash) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const scrollToAnchorTarget = (targetHash) => {
    const target = document.querySelector(targetHash);
    if (!target) return;

    if (!window.location.hash.startsWith(FILTER_HASH_PREFIX) && window.location.hash !== targetHash) {
      const url = new URL(window.location.href);
      url.hash = targetHash;
      const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      window.history.pushState({ ...currentState, collectionAnchorPilot: targetHash }, '', url);
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  if (window.location.hash === '#informationen') {
    setActiveLink('#informationen');
  }

  document.querySelectorAll(SORT_SELECTOR).forEach(enhanceSortControl);

  document.addEventListener('pointerdown', (event) => {
    sortWidgets.forEach((widget) => {
      if (!widget.field.contains(event.target)) closeSortWidget(widget);
    });
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches(SORT_SELECTOR)) return;
    const value = event.target.value;
    document.querySelectorAll(SORT_SELECTOR).forEach((select) => {
      if ([...select.options].some((option) => option.value === value)) select.value = value;
    });
    sortWidgets.forEach(updateSortWidget);
  });

  document.addEventListener('oe:filters:updated', () => sortWidgets.forEach(updateSortWidget));

  document.addEventListener('click', (event) => {
    const link = event.target.closest(NAV_LINK_SELECTOR);
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const targetHash = link.getAttribute('href');
    event.preventDefault();
    setActiveLink(targetHash);
    scrollToAnchorTarget(targetHash);
  });

  window.addEventListener('hashchange', () => {
    if (window.location.hash === '#artikel' || window.location.hash === '#informationen') {
      setActiveLink(window.location.hash);
    }
  });

  window.addEventListener('popstate', () => {
    if (window.location.hash === '#informationen') setActiveLink('#informationen');
    else if (!window.location.hash || window.location.hash === '#artikel') setActiveLink('#artikel');
  });
})();
